import { AxiosPromise, AxiosStatic } from "axios";
import {
  BankAccountDealsResponse,
  CreateUserBankAccountInput,
  PartialUpdateUserBankAccountInput,
  SetDefaultBankAccountInput,
  SetDefaultBankAccountResponse,
  UpdateUserBankAccountInput,
  UserBankAccount,
} from "../types/UserBankAccount";

export const getUserBankAccounts =
  (serverURL: string, appAxios: Pick<AxiosStatic, "get">) =>
  async (): AxiosPromise<UserBankAccount[]> =>
    await appAxios.get(`${serverURL}/user-bank-accounts`, {
      withCredentials: true,
    });

export const postUserBankAccount =
  (serverURL: string, appAxios: Pick<AxiosStatic, "post">) =>
  async (file: File, data: CreateUserBankAccountInput): AxiosPromise<void> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("accountType", data.accountType);
    formData.append("bankName", data.bankName);
    formData.append("accountName", data.accountName);
    formData.append("accountNumber", data.accountNumber);
    formData.append("bankCountry", data.bankCountry);
    if (data.sortCode) formData.append("sortCode", data.sortCode);
    if (data.accountNickname)
      formData.append("accountNickname", data.accountNickname);
    if (data.swift) formData.append("swift", data.swift);
    if (data.iban) formData.append("iban", data.iban);

    if (data.entityId) formData.append("entityId", data.entityId);
    if (data.routingNumber)
      formData.append("routingNumber", data.routingNumber);
    if (data.purposeCode) formData.append("purposeCode", data.purposeCode);
    if (data.currency) formData.append("currency", data.currency);

    return await appAxios.post(`${serverURL}/user-bank-accounts`, formData, {
      withCredentials: true,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  };

export const putUserBankAccount =
  (serverURL: string, appAxios: Pick<AxiosStatic, "put">) =>
  async (
    id: string,
    data: UpdateUserBankAccountInput,
    file?: File
  ): AxiosPromise<void> => {
    const formData = new FormData();

    if (file) formData.append("file", file);
    if (data.accountType) formData.append("accountType", data.accountType);
    if (data.bankName) formData.append("bankName", data.bankName);
    if (data.accountName) formData.append("accountName", data.accountName);
    if (data.accountNumber)
      formData.append("accountNumber", data.accountNumber);
    if (data.bankCountry) formData.append("bankCountry", data.bankCountry);
    if (data.sortCode !== undefined)
      formData.append("sortCode", data.sortCode ?? "");
    if (data.accountNickname !== undefined)
      formData.append("accountNickname", data.accountNickname ?? "");
    if (data.swift !== undefined) formData.append("swift", data.swift ?? "");
    if (data.iban !== undefined) formData.append("iban", data.iban ?? "");
    if (data.entityId) formData.append("entityId", data.entityId);
    if (data.routingNumber !== undefined)
      formData.append("routingNumber", data.routingNumber ?? "");
    if (data.purposeCode !== undefined)
      formData.append("purposeCode", data.purposeCode ?? "");
    if (data.currency !== undefined)
      formData.append("currency", data.currency ?? "");

    return await appAxios.put(
      `${serverURL}/user-bank-accounts/${id}`,
      formData,
      {
        withCredentials: true,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
  };

export const patchUserBankAccount =
  (serverURL: string, appAxios: Pick<AxiosStatic, "patch">) =>
  async (
    id: string,
    data: PartialUpdateUserBankAccountInput
  ): AxiosPromise<void> =>
    await appAxios.patch(`${serverURL}/user-bank-accounts/${id}`, data, {
      withCredentials: true,
    });

export const deleteUserBankAccount =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (id: string): AxiosPromise<void> =>
    await appAxios.delete(`${serverURL}/user-bank-accounts/${id}`, {
      withCredentials: true,
    });

export const setDefaultBankAccount =
  (serverURL: string, appAxios: Pick<AxiosStatic, "put">) =>
  async (
    id: string,
    data: SetDefaultBankAccountInput
  ): AxiosPromise<SetDefaultBankAccountResponse> =>
    await appAxios.put(`${serverURL}/user-bank-accounts/${id}/default`, data, {
      withCredentials: true,
    });

export const getBankAccountDeals =
  (serverURL: string, appAxios: Pick<AxiosStatic, "get">) =>
  async (id: string): AxiosPromise<BankAccountDealsResponse> =>
    await appAxios.get(`${serverURL}/user-bank-accounts/${id}/deals`, {
      withCredentials: true,
    });

/**
 * Gets the recommended bank account for a commitment.
 * The API applies filtering, currency matching, and status prioritization.
 *
 * @param commitmentId - The commitment ID to get recommendation for
 * @returns The recommended bank account, or null if none match criteria
 */
export const getRecommendedBankAccount =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (commitmentId: string): AxiosPromise<UserBankAccount | null> =>
    await appAxios.get(
      `${serverURL}/commitments-v2/${commitmentId}/recommended-bank-account`,
      {
        withCredentials: true,
      }
    );
